import React, { useCallback, useMemo, useRef, useState } from "react";
import { Table } from "../UI/Table";
import { StyledHolidayContainer } from "./styles";
import Button from "../UI/Button";
import { translate } from "../../utils/translations/translate";
import { ActionRenderer } from "./ActionRenderer";
import { FullScreenModal } from "../UI/Modal";
import { CreateEditHoliday } from "./CreateEditHoliday";
import { Loader } from "../Loader/Loader";
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";
import { Alert } from "../UI/Alert";
import { Dropdown } from "../UI/Dropdown";
import { generateHolidaysReport } from "../../utils/reports";
import { DATE_TIME_FORMAT } from "../../utils/constants";
import moment from "moment";
const dateFormatter = (params) => {
  if (params.value) {
    return moment(new Date(params.value)).format(DATE_TIME_FORMAT.DISPLAY);
  }
  return "";
};
export const HolidaySettings = ({
  holidaysData,
  toastInfo,
  onCreateEdit,
  onDeleteHoliday,
  isLoading,
  selectedYear,
  filterOptions,
  onYearChange,
  onToastClose
}) => {
  const [showCreateOrEditDialog, setShowCreateOrEditDialog] = useState(false);
  const [selectedHolidayData, setSelectedHolidayData] = useState(null);
  const holidayToBeDeletedRef = useRef(null);
  const handleEdit = useCallback((data) => {
    setSelectedHolidayData(data);
    setShowCreateOrEditDialog(true);
  }, []);
  const gridRef = useRef(null);
  const handleDelete = useCallback(({ holidayId }) => {
    holidayToBeDeletedRef.current = holidayId;
    setShowDeleteModal(true);
  }, []);
  const handleDeleteModalClose = useCallback(() => {
    setShowDeleteModal(false);
  }, []);
  const onConfirmHolidayDeletion = useCallback(() => {
    onDeleteHoliday(holidayToBeDeletedRef.current);
    setShowDeleteModal(false);
  }, [onDeleteHoliday]);
  const handleCreateOrEditSubmit = useCallback(
    (savedData) => {
      setShowCreateOrEditDialog(false);
      setSelectedHolidayData(null);
      onCreateEdit(savedData);
    },
    [onCreateEdit]
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const handleDownload = () => {
    const prepareListForDownload = holidaysData.map(holiday=>{
      const {holidayDate,holidayName,holidayDescription} = holiday;
      return {
        Name:holidayName,
        Description:holidayDescription,
        Date:moment(new Date(holidayDate)).format(DATE_TIME_FORMAT.DISPLAY)
      }
    });
    generateHolidaysReport(prepareListForDownload,selectedYear);
  };
  const columnDefs = useMemo(()=>{
    return [
      {
        headerName: "Name",
        field: "holidayName",
        sortable: true,
        filter: false,
        width: 200,
      },
      {
        headerName: "Date",
        field: "holidayDate",
        sortable: true,
        filter: false,
        width: 180,
        valueFormatter: dateFormatter,
      },
      {
        headerName: "Description",
        field: "holidayDescription",
        sortable: true,
        filter: false,
        flex:1,
      },
      {
        headerName: "Updated On",
        field: "holidayModifiedOn",
        sortable: true,
        filter: false,
        width: 180,
        cellRenderer: dateFormatter,
      },
      {
        headerName: "Actions",
        field: "action",
        sortable: false,
        filter: false,
        width: 80,
        cellRenderer: (params) => {
          const { data } = params;
          const newParams = { ...params };
          const { holidayDate } = data;
          const holiday = new Date(holidayDate);
          holiday.setHours(0, 0, 0, 0);
        
          const today = new Date();
          today.setHours(0, 0, 0, 0);
        
          const oneWeekAgo = new Date(today);
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
          if (holiday >= oneWeekAgo) {
            newParams.showEditIcon = true;
            newParams.showDeleteIcon = true;
          } else {
            newParams.showEditIcon = false;
            newParams.showDeleteIcon = false;
          }
          return <ActionRenderer {...newParams} />;
        },
        cellRendererParams: {
          showEditIcon: true,
          showDeleteIcon: true,
          onEdit: handleEdit,
          onDelete: handleDelete,
        },
      },
    ];
  },[handleDelete,handleEdit])

 const filteredColumns = useMemo(()=>{
  const isCurrentYear =  new Date().getFullYear() === selectedYear;
  if(isCurrentYear) return columnDefs;
  else{
    const newColumnDefs = columnDefs.filter(def=>def.field!=="action");
    return newColumnDefs;
  }
 },[columnDefs,selectedYear])
  return (
    <StyledHolidayContainer>
      {toastInfo && <Alert {...toastInfo} onToastClose={onToastClose} />}
      <header className="tma_holiday_header">
        {holidaysData.length > 0 && (
          <Tooltip title={translate("labels.downloadList")} arrow placement="top-start">
            <IconButton>
              <DownloadForOfflineIcon onClick={handleDownload} />
            </IconButton>
          </Tooltip>
         )}
        <Dropdown
          id="tma_years-filter"
          initialValue={selectedYear}
          options={filterOptions}
          onChange={({ value }) => {
            onYearChange(value);
          }}
        />
        <Button
          text={translate("labels.addNewHoliday")}
          variantStyle="primary"
          className={"tma_add_holiday"}
          onClick={() => setShowCreateOrEditDialog(true)}
        />
      </header>
      <div
        className="tma_holiday_table"
      >
        <Table
          columns={filteredColumns}
          rowSelection={"single"}
          rowData={holidaysData}
          showPagination={false}
          gridRef={gridRef}
        />
      </div>

      {showCreateOrEditDialog && (
        <FullScreenModal
          onClose={() => {
            setShowCreateOrEditDialog(false);
            setSelectedHolidayData(null);
          }}
        >
          <CreateEditHoliday
            isCreateMode={selectedHolidayData ? false : true}
            savedData={selectedHolidayData}
            onSave={handleCreateOrEditSubmit}
          />
        </FullScreenModal>
      )}
      {isLoading && <Loader />}
      {showDeleteModal && (
        <DeleteConfirmationModal
          title={translate("labels.deleteHoliday")}
          infoText={translate("labels.deleteHolidayMsg")}
          onClose={handleDeleteModalClose}
          onConfirm={onConfirmHolidayDeletion}
        />
      )}
    </StyledHolidayContainer>
  );
};
